import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Pagination,
  Select,
  TextField,
  Typography
} from '@mui/material';

import CloudOffIcon from '@mui/icons-material/CloudOff';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

import { FiltrosCuadrillas } from './FiltrosCuadrillas';
import { SubHeader } from '../../utils/componentes-utiles/SubHeader';
import { TablaCuadrillas } from './TablaCuadrillas';
import { getCuadrillas } from '../../services/apis';

/**
 * Devuelve YYYY-MM-DD usando zona local
 */
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Devuelve DD/MM/YYYY para mostrar bonito
 */
const formatDisplayDate = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

const normalizeDateOnly = (value) => {
  if (!value) return '';

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    const isoMatch = trimmedValue.match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (isoMatch) {
      return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
    }

    const esMatch = trimmedValue.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (esMatch) {
      return `${esMatch[3]}-${esMatch[2]}-${esMatch[1]}`;
    }
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return formatLocalDate(date);
};

const filterCuadrillasByDateRange = (items, desde, hasta) => {
  if (!Array.isArray(items)) return [];

  return items.filter((cuadrilla) => {
    const fechaCuadrilla = normalizeDateOnly(cuadrilla?.datos_cuadrilla?.fecha_cuadrilla);
    return fechaCuadrilla && fechaCuadrilla >= desde && fechaCuadrilla <= hasta;
  });
};

const getDefaultFechaDesde = () => {
  const date = new Date();
  date.setFullYear(date.getFullYear() );
  return formatLocalDate(date);
};

const getDefaultFechaHasta = () => {
  return formatLocalDate(new Date());
};

/**
 * Ajusta el rango para backend si luego quieres mandar datetime:
 * desde -> 2026-03-01 00:00:00
 * hasta -> 2026-03-27 23:59:59
 */
const buildRangeForApi = (desde, hasta) => {
  return {
    desde: `${desde} 00:00:00`,
    hasta: `${hasta} 23:59:59`,
  };
};

export const Cuadrillas = () => {
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [textoBusqueda, setTextoBusqueda] = useState('');

  // Fechas editables en inputs
  const [fechaDesde, setFechaDesde] = useState(getDefaultFechaDesde());
  const [fechaHasta, setFechaHasta] = useState(getDefaultFechaHasta());

  // Fechas aplicadas realmente al filtro / consulta
  const [filtroAplicado, setFiltroAplicado] = useState({
    fechaDesde: getDefaultFechaDesde(),
    fechaHasta: getDefaultFechaHasta(),
  });

  const [cuadrillas, setCuadrillas] = useState([]);
  const [cuadrillasOG, setCuadrillasOG] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [open, setOpen] = useState(null);
  const [openFiltros, setOpenFiltros] = useState(false);
  const [cantidadFiltros, setCantidadFiltros] = useState(0);
  const [errorFechas, setErrorFechas] = useState('');
  const latestRequestRef = useRef(0);

  const fechasValidas = useMemo(() => {
    if (!fechaDesde || !fechaHasta) return false;
    return fechaDesde <= fechaHasta;
  }, [fechaDesde, fechaHasta]);

  const cargarCuadrillas = async (desde, hasta) => {
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;

    try {
      setLoaded(false);
      setErrorFechas('');

      if (!desde || !hasta) {
        setCuadrillas([]);
        setCuadrillasOG([]);
        setErrorFechas('Debes indicar ambas fechas.');
        setLoaded(true);
        return;
      }

      if (desde > hasta) {
        setCuadrillas([]);
        setCuadrillasOG([]);
        setErrorFechas('La fecha desde no puede ser mayor que la fecha hasta.');
        setLoaded(true);
        return;
      }

      // Si tu API acepta solo YYYY-MM-DD, usa:
      const response = await getCuadrillas(desde, hasta);

      // Si tu API necesita datetime completos, usa esto en su lugar:
      // const rango = buildRangeForApi(desde, hasta);
      // const response = await getCuadrillas(rango.desde, rango.hasta);

      if (response?.success) {
        if (latestRequestRef.current !== requestId) {
          return;
        }

        const data = filterCuadrillasByDateRange(response.data || [], desde, hasta);
        setCuadrillas(data);
        setCuadrillasOG(data);
      } else {
        if (latestRequestRef.current !== requestId) {
          return;
        }

        setCuadrillas([]);
        setCuadrillasOG([]);
      }
    } catch (error) {
      if (latestRequestRef.current !== requestId) {
        return;
      }

      console.error('Error al cargar cuadrillas:', error);
      setCuadrillas([]);
      setCuadrillasOG([]);
    } finally {
      if (latestRequestRef.current === requestId) {
        setLoaded(true);
      }
    }
  };

  useEffect(() => {
    cargarCuadrillas(filtroAplicado.fechaDesde, filtroAplicado.fechaHasta);
  }, [filtroAplicado]);

  useEffect(() => {
    setCurrentPage(1);
  }, [textoBusqueda]);

  const aplicarFiltroFechas = () => {
    if (!fechaDesde || !fechaHasta) {
      setErrorFechas('Debes indicar ambas fechas.');
      return;
    }

    if (fechaDesde > fechaHasta) {
      setErrorFechas('La fecha desde no puede ser mayor que la fecha hasta.');
      return;
    }

    setErrorFechas('');
    setCurrentPage(1);
    setFiltroAplicado({
      fechaDesde,
      fechaHasta,
    });
  };

  const limpiarFiltroFechas = () => {
    const desde = getDefaultFechaDesde();
    const hasta = getDefaultFechaHasta();

    setFechaDesde(desde);
    setFechaHasta(hasta);
    setTextoBusqueda('');
    setErrorFechas('');
    setCurrentPage(1);

    setFiltroAplicado({
      fechaDesde: desde,
      fechaHasta: hasta,
    });
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const actualizarAvatarRecolector = (lista, idCuadrilla, idUsuario, imagenUrl) => {
    return lista.map((cuadrilla) => {
      if (cuadrilla?.datos_cuadrilla?.id_cuadrilla !== idCuadrilla) {
        return cuadrilla;
      }

      const actualizarGrupo = (grupo = []) =>
        grupo.map((recolector) =>
          recolector?.id_usuario === idUsuario
            ? { ...recolector, imagen_url: imagenUrl }
            : recolector
        );

      return {
        ...cuadrilla,
        recolectores: {
          ...cuadrilla.recolectores,
          no_comprobados: actualizarGrupo(cuadrilla.recolectores?.no_comprobados),
          comprobados: actualizarGrupo(cuadrilla.recolectores?.comprobados),
          error_comprobacion: actualizarGrupo(cuadrilla.recolectores?.error_comprobacion),
        },
      };
    });
  };

  const handleRecolectorAvatarUploaded = ({ idCuadrilla, idUsuario, imagenUrl }) => {
    setCuadrillas((currentCuadrillas) =>
      actualizarAvatarRecolector(currentCuadrillas, idCuadrilla, idUsuario, imagenUrl)
    );
    setCuadrillasOG((currentCuadrillas) =>
      actualizarAvatarRecolector(currentCuadrillas, idCuadrilla, idUsuario, imagenUrl)
    );
  };

  const cuadrillasFiltradas = useMemo(() => {
    const termino = textoBusqueda.trim().toLowerCase();
    if (!termino) return cuadrillas;

    return cuadrillas.filter((cuadrilla) => {
      const datos = cuadrilla?.datos_cuadrilla ?? {};
      const campos = [
        datos.id_cuadrilla,
        datos.nombre_cuadrilla,
        datos.nombre_cabo,
        datos.apellidos_cabo,
        datos.nombre_cliente,
      ];

      return campos.some((campo) =>
        String(campo ?? '').toLowerCase().includes(termino)
      );
    });
  }, [cuadrillas, textoBusqueda]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCuadrillas = cuadrillasFiltradas.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <Grid
      container
      direction="row"
      justifyContent="center"
      alignItems="center"
      size={{ xs: 12 }}
    >
      <Grid
        container
        direction="column"
        justifyContent="start"
        alignItems="start"
        size={{ xs: 12, md: 11.5 }}
        sx={{ mb: 1 }}
      >
        <Grid item size={{ xs: 12 }}>
          <SubHeader titulo={'Partes de grupos'} />
        </Grid>

        <Grid item size={{ xs: 12 }}>
          <Grid
            container
            direction="row"
            justifyContent="start"
            alignItems="end"
            size={{ xs: 12 }}
            spacing={2}
            sx={{ px: 2 }}
          >
            <TextField
              variant="standard"
              label="Fecha desde"
              type="date"
              size="small"
              value={fechaDesde}
              onChange={(event) => setFechaDesde(event.target.value)}
              error={!!errorFechas && !fechasValidas}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              variant="standard"
              label="Fecha hasta"
              type="date"
              size="small"
              value={fechaHasta}
              onChange={(event) => setFechaHasta(event.target.value)}
              error={!!errorFechas && !fechasValidas}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              variant="standard"
              label="Buscar grupo"
              placeholder="ID, nombre, cabo o empresa"
              size="small"
              value={textoBusqueda}
              onChange={(event) => setTextoBusqueda(event.target.value)}
            />

            <Button
              variant="contained"
              onClick={aplicarFiltroFechas}
              disabled={!fechaDesde || !fechaHasta}
            >
              Buscar
            </Button>

            <Button variant="outlined" onClick={limpiarFiltroFechas}>
              Limpiar
            </Button>

            <Badge badgeContent={cantidadFiltros} color="primary">
              <Button
                variant="outlined"
                onClick={() => {
                  setOpenFiltros(true);
                }}
              >
                <FilterAltIcon sx={{ fontSize: 20, mr: 0.3, mt: -0.3 }} />
                filtros
              </Button>
            </Badge>

            <Button variant="outlined">
              <FileDownloadIcon sx={{ fontSize: 20, mr: 0.3, mt: -0.3 }} />
              Exportar
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        size={{ xs: 12, md: 11.5 }}
        sx={{ mt: 1, mb: 1 }}
      >
        <Grid item size={{ xs: 12 }}>
          <Typography variant="body2" color="text.secondary">
            Rango aplicado:{' '}
            <strong>{formatDisplayDate(filtroAplicado.fechaDesde)}</strong>
            {' - '}
            <strong>{formatDisplayDate(filtroAplicado.fechaHasta)}</strong>
          </Typography>
        </Grid>

        {!!errorFechas && (
          <Grid item size={{ xs: 12 }} sx={{ mt: 1 }}>
            <Alert severity="warning">{errorFechas}</Alert>
          </Grid>
        )}
      </Grid>

      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="start"
        size={{ xs: 12, md: 11.5 }}
      >
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
          size={{ xs: 12 }}
        >
          {loaded ? (
            <Grid
              container
              direction="row"
              justifyContent={cuadrillasFiltradas.length > 0 ? 'start' : 'center'}
              alignItems={cuadrillasFiltradas.length > 0 ? 'start' : 'center'}
              size={{ xs: 12 }}
              className="fade-in barra-scroll"
              sx={{ overflow: 'auto', minHeight: '55vh', maxHeight: '70vh' }}
            >
              {cuadrillasFiltradas.length > 0 ? (
                <TablaCuadrillas
                  currentCuadrillas={currentCuadrillas}
                  setOpen={setOpen}
                  open={open}
                  onRecolectorAvatarUploaded={handleRecolectorAvatarUploaded}
                />
              ) : (
                <Grid
                  container
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  size={{ xs: 12 }}
                  className="fade-in"
                >
                  <Alert
                    icon={<CloudOffIcon fontSize="inherit" color="warning" />}
                    severity="warning"
                  >
                    No se encontraron grupos.
                  </Alert>
                </Grid>
              )}
            </Grid>
          ) : (
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
              size={{ xs: 12 }}
              sx={{ py: 3, minHeight: '50vh' }}
              className="fade-in"
            >
              <LinearProgress sx={{ width: '100%' }} />
            </Grid>
          )}
        </Grid>

        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="end"
          size={{ xs: 12 }}
          sx={{ mt: 1, display: cuadrillasFiltradas.length > 0 ? 'flex' : 'none' }}
        >
          <Grid
            container
            direction="row"
            justifyContent="start"
            alignItems="center"
            size={{ xs: 12, md: 3 }}
          >
            <Grid item size={{ xs: 4 }}>
              <FormControl variant="standard" fullWidth>
                <InputLabel id="filas-por-pagina-label">Filas por página</InputLabel>
                <Select
                  labelId="filas-por-pagina-label"
                  id="filas-por-pagina"
                  value={itemsPerPage}
                  label="Filas por página"
                  onChange={(event) => {
                    setItemsPerPage(Number(event.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={75}>75</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Pagination
            count={Math.ceil(cuadrillasFiltradas.length / itemsPerPage)}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            sx={{
              display:
                cuadrillasFiltradas.length > 0 && itemsPerPage < cuadrillasFiltradas.length
                  ? 'flex'
                  : 'none',
            }}
          />
        </Grid>
      </Grid>

      <FiltrosCuadrillas
        open={openFiltros}
        setOpen={setOpenFiltros}
        cuadrillas={cuadrillas}
        setCuadrillas={setCuadrillas}
        cuadrillasOG={cuadrillasOG}
        cantidadFiltros={cantidadFiltros}
        setCantidadFiltros={setCantidadFiltros}
      />
    </Grid>
  );
};
